import React, {Component, createRef} from 'react';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';
import {ElementGroup, Indent} from '@pb/kit-component-element-group';

import {GoToPage} from '../go-to-page/go-to-page.component';
import {PaginationRangeTypes} from '../typedef';
import {
    FIRST_PAGE,
    MAX_VISIBLE,
    MAX_VISIBLE_SMALL_RANGE,
    NEXT_CAPTION,
    NUMBER_CAPTION,
    PREV_CAPTION,
} from './constants';
import {
    NavigationButton,
    PaginationButton,
    PaginationInternalButtons,
    PaginationSeparator,
} from './pagination-buttons.component';

import styles from './pagination.component.module.styl';

import type {PaginationRangeType} from '../typedef';
import type {PaginationStripeProps} from '../pagination-stripe/pagination-stripe.component';

const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

/**
 * Pagination props
 */
export type PaginationProps = {
    /**
     * Current page value
     */
    currentPage: number,
    /**
     * Called after change page
     */
    onChange: (currentPage: number, entryPoint: string) => void,
    /**
     * Total pages count
     */
    totalPages: number,
    /**
     * Disabled pagination
     */
    disabled?: boolean,
    /**
     * Whether to show the input Go To
     */
    goToPage?: boolean,
    /**
     * Defines buttons size
     */
    size?: PaginationStripeProps['size'],
    /**
     * The width of parent container where pagination uses (number of pixels)
     */
    maxWidth?: number,
    /**
     * Range value `SMALL` or `NORMAL`
     */
    range?: PaginationRangeType,
    /**
     * Whether to show the last page
     */
    showLastPage?: boolean,
    /**
     * Whether to use only props to change the current page
     */
    usePropsOnly?: boolean,
};

type State = {
    currentPage: number,
    smallContainerFitConfig?: {
        isPaginationFit: boolean,
        fromPage: number,
    },
};

type ShowSeparatorsParams = {
    currentPage: number,
    range: PaginationRangeType,
    showLastPage: boolean,
    totalPages: number,
};

const showSeparators = ({currentPage, range, totalPages, showLastPage}: ShowSeparatorsParams) => {
    const delta = totalPages - range;

    if (range === PaginationRangeTypes.SMALL) {
        const showSecondSeparatorCondition = showLastPage ? currentPage < delta : currentPage <= delta;
        const isSeparatorVisible = showLastPage
            ? totalPages > MAX_VISIBLE_SMALL_RANGE
            : totalPages >= Math.min(MAX_VISIBLE_SMALL_RANGE, range);
        return {
            showFirstSeparator: currentPage > range + 1 && isSeparatorVisible && delta > range,
            showSecondSeparator: showSecondSeparatorCondition && isSeparatorVisible,
        };

    } else {
        const isSeparatorVisible = showLastPage
            ? totalPages > MAX_VISIBLE
            : totalPages >= (MAX_VISIBLE - 1);
        return {
            showFirstSeparator: currentPage > range && isSeparatorVisible && delta >= range,
            showSecondSeparator: currentPage <= delta && isSeparatorVisible,
        };
    }
};

/**
 * @description The `Pagination` component is used to display a list of page numbers and navigation buttons.
 *
 * ## Features
 * - Provides navigation through different pages of content.
 *
 * * ## Accessibility
 * - Pagination should be wrapped in a `nav` element with an `aria-label` attribute when used without the `PaginationStripe` component.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/components-pagination--pagination-default}
 **/


export class Pagination extends Component<PaginationProps, State> {
    paginationContainerRef: React.RefObject<HTMLUListElement | null>;

    static defaultProps = {
        goToPage: false,
        onChange: () => undefined,
        range: PaginationRangeTypes.NORMAL,
        size: 'normal' as const,
        showLastPage: true,
    };

    constructor(props: PaginationProps) {
        super(props);
        this.paginationContainerRef = createRef();
    }

    state: State = {
        currentPage: this.props.currentPage,
        smallContainerFitConfig: {
            isPaginationFit: true,
            fromPage: NaN,
        },
    };

    componentDidMount() {
        const {maxWidth} = this.props;

        if (maxWidth) {
            const observer = new ResizeObserver(([entry]) => {
                if (entry.contentRect.width > maxWidth && this.state.smallContainerFitConfig?.isPaginationFit) {
                    this.setState({
                        smallContainerFitConfig: {
                            isPaginationFit: false,
                            fromPage: this.props.currentPage,
                        },
                    });
                }
                if (entry.contentRect.width < maxWidth && !this.state.smallContainerFitConfig?.isPaginationFit
                    && this.state.smallContainerFitConfig?.fromPage !== this.props.currentPage) {
                    this.setState({
                        smallContainerFitConfig: {
                            isPaginationFit: true,
                            fromPage: this.props.currentPage,
                        },
                    });
                }
            });
            if (this.paginationContainerRef && this.paginationContainerRef.current) {
                observer.observe(this.paginationContainerRef.current);
            }
        }
    }

    componentDidUpdate(prevProps: PaginationProps) {
        if (prevProps.currentPage !== this.props.currentPage) {
            this.setState({currentPage: this.props.currentPage});
        }
    }

    changePage = (page: number, entryPoint?: string) => {
        const {totalPages, usePropsOnly} = this.props;
        let currentPage;
        if (page < 1) {
            currentPage = 1;
        } else {
            currentPage = page > totalPages ? totalPages : page;
        }
        if (!usePropsOnly) {
            this.setState({currentPage});
        }
        this.props.onChange(currentPage, entryPoint || NUMBER_CAPTION);
    };

    onNext = () => {
        const currentPage = this.state.currentPage + 1;
        if (!this.props.usePropsOnly) {
            this.setState({currentPage});
        }
        this.props.onChange(currentPage, NEXT_CAPTION);
    };

    onPrevious = () => {
        const currentPage = this.state.currentPage - 1;
        if (!this.props.usePropsOnly) {
            this.setState({currentPage});
        }
        this.props.onChange(currentPage, PREV_CAPTION);
    };

    render() {
        const {
            disabled,
            goToPage,
            size = 'normal',
            range = PaginationRangeTypes.NORMAL,
            showLastPage = true,
            totalPages,
        } = this.props;
        const {currentPage} = this.state;

        if (totalPages > FIRST_PAGE) {
            const {showFirstSeparator, showSecondSeparator} = showSeparators({
                currentPage, range, totalPages, showLastPage,
            });

            const prevButtonClasses = classNames('pagination__prev', {
                pagination__prev_small: size === 'small',
            });

            const nextButtonClasses = classNames('pagination__next', {
                pagination__next_small: size === 'small',
            });

            const {smallContainerFitConfig} = this.state;

            return (
                <ElementGroup indent={Indent.NONE}>
                    <ul ref={this.paginationContainerRef} className={classes.pagination}>
                        <NavigationButton
                            className={prevButtonClasses}
                            caption={PREV_CAPTION}
                            isDisabled={currentPage === 1 || disabled}
                            size={size}
                            onClick={this.onPrevious}
                        />

                        <PaginationButton
                            currentPage={currentPage}
                            isDisabled={disabled}
                            size={size}
                            onClick={this.changePage}
                            page={FIRST_PAGE}
                        />

                        <PaginationSeparator disabled={disabled} shown={showFirstSeparator} size={size}/>

                        <PaginationInternalButtons
                            currentPage={currentPage}
                            isDisabled={disabled}
                            size={size}
                            isNeedToFit={!smallContainerFitConfig?.isPaginationFit}
                            onClick={this.changePage}
                            range={range}
                            totalPages={totalPages}
                        />

                        <PaginationSeparator disabled={disabled} shown={showSecondSeparator} size={size}/>

                        {(showLastPage || currentPage + range > totalPages) ? (
                            <PaginationButton
                                currentPage={currentPage}
                                isDisabled={disabled}
                                size={size}
                                onClick={this.changePage}
                                page={totalPages}
                            />
                        ) : null}

                        <NavigationButton
                            className={nextButtonClasses}
                            caption={NEXT_CAPTION}
                            isDisabled={currentPage === totalPages || disabled}
                            size={size}
                            onClick={this.onNext}
                        />
                    </ul>
                    {goToPage ? <GoToPage onChange={this.changePage}/> : null}
                </ElementGroup>
            );
        }

        return null;
    }
}
